import { ConfirmModal } from '@vrplatform/components';
import {
  captureSentryError,
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import {
  showErrorNotification,
  showSuccessNotification,
} from '@vrplatform/theme';
import {
  showLoadingNotification,
  showWarnNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';
import { useDebouncedValue } from '@mantine/hooks';
import { useId } from 'react';
import type { DepositRow } from './useDepositsTableQuery';

interface ModalProps {
  opened: boolean;
  closeModal: () => void;
  deposit: Maybe<DepositRow>;
}

export const DepositEllipsisMenuModals = ({
  deleteModal,
  unreconcileModal,
  archiveModal,
  deposit: initial,
}: {
  deposit: ModalProps['deposit'];
  deleteModal: {
    opened: boolean;
    closeModal: () => void;
    onSuccess?: () => void;
  };
  unreconcileModal: {
    opened: boolean;
    closeModal: () => void;
  };
  archiveModal: {
    opened: boolean;
    closeModal: () => void;
  };
}) => {
  const [debounced] = useDebouncedValue(initial, 500);

  const deposit = initial || debounced;

  return (
    <>
      <Delete {...deleteModal} deposit={deposit} />
      <Unreconcile {...unreconcileModal} deposit={deposit} />
      <Archive {...archiveModal} deposit={deposit} />
    </>
  );
};

const Delete = ({
  opened,
  closeModal,
  deposit,
  onSuccess,
}: ModalProps & {
  onSuccess?: () => void;
}) => {
  const depositId = deposit?.id;

  const invalidate = useInvalidateApiQueries();

  const { mutateAsync: mutate, isPending: loading } = useApiMutation(
    'delete',
    '/transactions/{id}'
  );

  const submit = async (isSecondConfirm: boolean) => {
    if (!depositId) {
      captureSentryError('Deposit to delete missing');
      showWarnNotification({
        title: 'Missing expense',
        message:
          'Expense to delete missing. Please reach out to support if the issue persists.',
      });

      return;
    }

    return await mutate({
      params: {
        path: {
          id: depositId,
        },
        query: {
          onLocked: isSecondConfirm ? 'archive' : 'error',
        },
      },
    })
      .then((res) => {
        showSuccessNotification({
          title: 'Success!',
          message: `Deposit was ${res.data.status} successfully.`,
        });

        onSuccess?.();

        closeModal();
        invalidate(['deposits']);
        invalidate(['accounts']);
      })
      .catch((err) => {
        if (
          err?.message.includes('pass onLocked=archive to archive') ||
          err?.message.includes('has journal entries') ||
          err?.message.includes('has reconciled bank records')
        ) {
          if (deposit.status === 'inactive') {
            showErrorNotification({
              title: 'Data associated with this deposit',
              message:
                'This deposit cannot be deleted because it has data associated with it.',
              icon: null,
              color: 'yellow',
            });
            closeModal();
          } else {
            return {
              showConfirm: true,
            };
          }
        } else {
          showApiErrorNotification({
            error: err,
            title: 'Failed to delete deposit',
            defaultMessage:
              'We failed to delete deposit. Please reach out to support if the issue persists.',
          });
        }
      });
  };

  return (
    <ConfirmModal
      type="delete"
      opened={opened}
      closeModal={closeModal}
      loading={loading}
      onSubmit={submit}
      title={'Delete this deposit?'}
      subtitle={
        'Are you sure you want to delete this deposit? This action cannot be undone.'
      }
      secondConfirm={{
        title: 'We have found data associated with this deposit',
        subtitle:
          'We have found data associated with this deposit. Would you like to archive the deposit instead? This will keep the data associated with the deposit, but will remove the deposit from your dashboard.',
        submitTitle: 'Archive deposit',
      }}
    />
  );
};

const Unreconcile = ({ opened, closeModal, deposit }: ModalProps) => {
  const notifyId = useId();
  const bankRecordId = deposit?.payment.bankRecordIds?.[0];

  const invalidate = useInvalidateApiQueries([
    'bank-records',
    'deposits',
    'accounts',
  ]);

  const { mutateAsync, isPending: loading } = useApiMutation(
    'put',
    '/bank-records/{id}'
  );

  const submit = async () => {
    if (!bankRecordId)
      return showWarnNotification({ message: 'Missing bank record' });

    showLoadingNotification({ id: notifyId });

    await mutateAsync({
      params: {
        path: {
          id: bankRecordId,
        },
      },
      body: {
        reconciledTransactionIds: [],
      },
    })
      .then(() => {
        updateSuccessNotification({
          id: notifyId,
          title: 'Success!',
          message: 'Deposit was unmatched successfully.',
        });
        closeModal();
        invalidate();
      })
      .catch((error: any) => {
        showApiErrorNotification({
          id: notifyId,
          title: 'Failed to unmatch deposit',
          defaultMessage:
            'We failed to unmatch the deposit. Please try again later and if the problem persists, contact support.',
          error,
        });
      });
  };

  return (
    <ConfirmModal
      type="confirm"
      opened={opened}
      closeModal={closeModal}
      loading={loading}
      onSubmit={submit}
      title={'Are you sure you want to unmatch this deposit?'}
      subtitle={
        'This action will remove the deposit from the bank record and allow you to match it again.'
      }
    />
  );
};

const Archive = ({ deposit: initial, opened, closeModal }: ModalProps) => {
  const invalidate = useInvalidateApiQueries(['deposits']);

  const { mutateAsync: update, isPending: loading } = useApiMutation(
    'put',
    '/transactions/{id}'
  );

  const [debounced] = useDebouncedValue(initial, 500);

  const deposit = debounced || initial;

  const isArchived = deposit?.status === 'inactive';

  const handle = async () => {
    if (!deposit) {
      captureSentryError('Deposit to update missing');
      return showWarnNotification({
        title: 'Missing deposit',
        message:
          'Deposit to update missing. Please reach out to support if the issue persists.',
      });
    }

    return await update({
      params: {
        path: {
          id: deposit.id,
        },
      },
      body: {
        status: deposit.status === 'inactive' ? 'active' : 'inactive',
      },
    })
      .then(() => {
        invalidate();
      })
      .catch((error: any) => {
        showApiErrorNotification({
          error,
          title: 'Failed to update deposit',
          defaultMessage:
            'We failed to update deposit. Please reach out to support if the issue persists.',
        });
      })
      .finally(() => {
        closeModal();
      });
  };

  return (
    <ConfirmModal
      type="confirm"
      opened={opened}
      closeModal={closeModal}
      loading={loading}
      size={400}
      onSubmit={() => handle()}
      title={
        isArchived
          ? 'Would you like to enable the deposit?'
          : 'Are you sure you want to archive the deposit?'
      }
      subtitle={
        isArchived
          ? 'This will make the deposit visible again in the deposits list and count its journal entries to your accounting.'
          : 'This will hide the deposit from the deposits list and exclude its journal entries from your accounting.'
      }
      submitTitle={isArchived ? 'Enable' : 'Archive'}
      icon={isArchived ? 'RefreshCwIcon' : 'ArchiveIcon'}
    />
  );
};
